/**
 * Auto Event Tracking — Minimal SaaS Standard
 *
 * Automatically captures only essential events:
 *   - Page views (route changes)
 *   - Uncaught errors / unhandled rejections
 *
 * Everything else (feature usage, file ops, AI interactions) is tracked
 * explicitly where the action happens via analytics.featureUsed() / analytics.track().
 *
 * Removed (noisy, low-value events):
 *   - Button click tracking (every single click)
 *   - Typing start/stop
 *   - Content change tracking
 *   - Idle/active detection
 *   - Page visibility changes
 *   - Performance.timing metrics
 *   - Session heartbeats (every 30s)
 *   - Console.error interception
 *   - Auto-save DOM polling
 */

import { analytics } from './analyticsService';

class AutoEventTracker {
  constructor() {
    this.isInitialized = false;
  }

  initialize() {
    if (this.isInitialized) return;

    this.trackNavigation();
    this.trackErrors();

    this.isInitialized = true;
  }

  /**
   * Track route changes (page views).
   * Intercepts history.pushState to capture React Router navigations.
   */
  trackNavigation() {
    const normalizeRoute = (pathname) =>
      pathname
        .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id')
        .replace(/\/\d+/g, '/:id');

    let previousRoute = window.location.pathname;

    const originalPushState = window.history.pushState;
    window.history.pushState = function (...args) {
      const newRoute = args[2] || window.location.pathname;
      originalPushState.apply(this, args);

      analytics.pageView(normalizeRoute(newRoute), {
        from: normalizeRoute(previousRoute),
      });
      previousRoute = newRoute;
    };

    // Browser back/forward
    window.addEventListener('popstate', () => {
      analytics.pageView(normalizeRoute(window.location.pathname), {
        navigationType: 'popstate',
      });
    });
  }

  /**
   * Track uncaught errors and unhandled promise rejections.
   * These are critical — they indicate app crashes.
   */
  trackErrors() {
    window.addEventListener('error', (event) => {
      analytics.trackError('uncaught_error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      analytics.trackError('unhandled_rejection', {
        reason: event.reason?.message || String(event.reason),
      });
    });
  }

  destroy() {
    this.isInitialized = false;
  }
}

const autoTracker = new AutoEventTracker();
export default autoTracker;
